import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../services/api';

interface AccountSettings {
  companyName: string;
  subdomain: string;
  defaultLanguage: string;
  supportedLanguages: string[];
  brandColor: string | null;
}

async function fetchAccountSettings(): Promise<AccountSettings> {
  const { data } = await apiClient.get<AccountSettings>('/account');
  return data;
}

// ── Colour utilities ──────────────────────────────────────────────────────────

function hexToHsl(hex: string): [number, number, number] | null {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, Math.round(l * 100)];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

function hslToHex(h: number, s: number, l: number): string {
  const ll = l / 100;
  const ss = s / 100;
  const a = ss * Math.min(ll, 1 - ll);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const c = ll - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * c).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Derives the full DS brand token set from a single hex brand colour.
 */
function deriveBrandVars(brandColor: string): Record<string, string> {
  if (!/^#[0-9a-fA-F]{6}$/.test(brandColor)) return {};
  const hsl = hexToHsl(brandColor);
  if (!hsl) return {};
  const [h, s, l] = hsl;
  return {
    '--brand-primary':         brandColor,
    '--brand-primary-hover':   hslToHex(h, s, clamp(l - 8, 0, 100)),
    '--brand-primary-pressed': hslToHex(h, s, clamp(l - 18, 0, 100)),
    '--brand-primary-soft':    hslToHex(h, clamp(s - 20, 10, 60), clamp(96, 0, 100)),
    '--brand-on-primary':      l > 55 ? '#111827' : '#ffffff',
    '--brand-on-soft':         hslToHex(h, s, clamp(l - 25, 10, 50)),
  };
}

function applyVars(vars: Record<string, string>) {
  const root = document.documentElement;
  Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
}

function clearBrandVars() {
  const root = document.documentElement;
  [
    '--brand-primary', '--brand-primary-hover', '--brand-primary-pressed',
    '--brand-primary-soft', '--brand-on-primary', '--brand-on-soft',
  ].forEach(k => root.style.removeProperty(k));
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetches the tenant's account settings, then applies (or clears) brand CSS
 * custom properties on <html> so every DS component picks up the tenant colour.
 */
export function useBrandTheme() {
  const { data } = useQuery({
    queryKey: ['account-settings'],
    queryFn: fetchAccountSettings,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    if (!data) return;
    if (data.brandColor) {
      applyVars(deriveBrandVars(data.brandColor));
    } else {
      clearBrandVars();
    }
  }, [data?.brandColor]);

  return data ?? null;
}
